import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { getOperationsSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import {
  saveOperations,
  setStretcherChairMovingTo,
  setJointCommissionTimeOut,
  setRightPatient,
  setRightProcedure,
  setRightConsent,
  setRightSiteSide,
  setRightPosition,
  setComplete,
  setSafetyBrief,
  setLastName,
  setFirstName,
  setWeight,
  setWeightUnit,
  setHearingPrecautions,
  setEnvironmentalControls,
  setSafetyRestraints,
  setSeizurePrecautions,
  setPatientLoadedCold,
  setPatientLoadedHot,
  setPatientOffloadCold,
  setPatientOffloadHot,
  setPatientMonitoring,
  setPediatricStretcherChair,
  setPediatricSafetyRestraintDevice,
  setBabyPod,
  setPediMate,
  setPatientPersonalProtectiveEquipment,
  setUpdateInputRows,
} from '../store/operations.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { OperationsConstants } from '../store/operations.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import Procedure from '../../../../components/procedure/Procedure.component'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { getOperationsOptionsSelector } from '../../../options/selectors'
import { IOption } from '../../../../common/common.types'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialOperationsState } from '../store/operations.reducers'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import './operations.page.css'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import { goBack } from '../../../../utilities/navigation.util'
import { IAlertState } from '../../../../components/alert/store/alert.types'

const Operations: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const operations = useSelector(getOperationsSelector)
  const {
    date,
    time,
    assessedBy,
    successfulAttempt,
    pta,
    stretcherChairMovingTo,
    jointCommissionTimeOut,
    rightPatient,
    rightProcedure,
    rightConsent,
    rightSiteSide,
    rightPosition,
    complete,
    safetyBrief,
    lastName,
    firstName,
    weight,
    weightUnit,
    hearingPrecautions,
    environmentalControls,
    safetyRestraints,
    seizurePrecautions,
    patientLoadedCold,
    patientLoadedHot,
    patientOffloadCold,
    patientOffloadHot,
    patientMonitoring,
    pediatricStretcherChair,
    pediatricSafetyRestraintDevice,
    babyPod,
    pediMate,
    patientPersonalProtectiveEquipment,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    attempts,
    rolePerforming,
    rolePerformingPNNV,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = operations
  const operationsOptions = useSelector(getOperationsOptionsSelector)
  const { weightUnitOptions } = operationsOptions
  const serviceConfig: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DEFAULT_WEIGHT_MEASUREMENT, DISPLAY_SECONDS } = serviceConfig
  const displaySeconds = !!DISPLAY_SECONDS

  useEffect(() => {
    if (!weightUnit) {
      const defaultWeightUnits = weightUnitOptions.find(
        (option: IOption) => option.name?.toString() === DEFAULT_WEIGHT_MEASUREMENT
      )
      if (defaultWeightUnits) dispatch(setWeightUnit(defaultWeightUnits))
    }
  }, [])

  const save = () => {
    dispatch(saveOperations())
  }

  const lastNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setLastName(e.detail.value))
  }

  const firstNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setFirstName(e.detail.value))
  }

  const weightOnChange = (values: string[]) => {
    dispatch(setWeight(values.join('')))
    save()
  }

  const weightUnitOnChange = (option: IOption) => {
    dispatch(setWeightUnit(option))
    save()
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'operations/SET_TIME', time: time })
              dispatch({ type: 'operations/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: operations.key,
                rowActionType: TimelineConstants.SET_OPERATIONS_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_STRETCHER_CHAIR_MOVING_TO,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.stretcherChairMovingTo'),
            dataTestId: 'StretcherChairMovingTo',
            isChecked: stretcherChairMovingTo,
            onChangeAction: setStretcherChairMovingTo,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_JOINT_COMMISSION_TIME_OUT,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.jointCommissionTimeOut'),
            dataTestId: 'JointCommissionTimeOut',
            isChecked: jointCommissionTimeOut,
            onChangeAction: setJointCommissionTimeOut,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_RIGHT_PATIENT,
          type: ProcedureConstants.TOGGLE,
          hidden: true,
          props: {
            rightLabel: t('operations.rightPatient'),
            dataTestId: 'RightPatient',
            isChecked: rightPatient,
            onChangeAction: setRightPatient,
            className: 'nested-toggle',
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_RIGHT_PROCEDURE,
          type: ProcedureConstants.TOGGLE,
          hidden: true,
          props: {
            rightLabel: t('operations.rightProcedure'),
            dataTestId: 'RightProcedure',
            isChecked: rightProcedure,
            onChangeAction: setRightProcedure,
            className: 'nested-toggle',
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_RIGHT_CONSENT,
          type: ProcedureConstants.TOGGLE,
          hidden: true,
          props: {
            rightLabel: t('operations.rightConsent'),
            dataTestId: 'RightConsent',
            isChecked: rightConsent,
            onChangeAction: setRightConsent,
            className: 'nested-toggle',
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_RIGHT_SITE_SIDE,
          type: ProcedureConstants.TOGGLE,
          hidden: true,
          props: {
            rightLabel: t('operations.rightSiteSide'),
            dataTestId: 'RightSiteSide',
            isChecked: rightSiteSide,
            onChangeAction: setRightSiteSide,
            className: 'nested-toggle',
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_RIGHT_POSITION,
          type: ProcedureConstants.TOGGLE,
          hidden: true,
          props: {
            rightLabel: t('operations.rightPosition'),
            dataTestId: 'RightPosition',
            isChecked: rightPosition,
            onChangeAction: setRightPosition,
            className: 'nested-toggle',
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_COMPLETE,
          type: ProcedureConstants.TOGGLE,
          hidden: true,
          props: {
            rightLabel: t('operations.complete'),
            dataTestId: 'Complete',
            isChecked: complete,
            onChangeAction: setComplete,
            className: 'nested-toggle',
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_SAFETY_BRIEF,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.safetyBrief'),
            dataTestId: 'SafetyBrief',
            isChecked: safetyBrief,
            onChangeAction: setSafetyBrief,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_LAST_NAME,
          type: ProcedureConstants.TEXT,
          hidden: true,
          props: {
            label: t('operations.lastName'),
            dataTestID: 'LastName',
            inputValue: lastName,
            onChangeAction: lastNameOnChange,
          },
        },
        {
          key: OperationsConstants.SET_FIRST_NAME,
          type: ProcedureConstants.TEXT,
          hidden: true,
          props: {
            label: t('operations.firstName'),
            dataTestID: 'FirstName',
            inputValue: firstName,
            onChangeAction: firstNameOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_WEIGHT,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: true,
          props: {
            label: t('operations.weight'),
            dataTestId: 'Weight',
            values: weight ? weight.split('') : [],
            onNumPadClick: weightOnChange,
            validCharacters: numberPadKeysWithDecimal,
          },
        },
        {
          key: OperationsConstants.SET_WEIGHT_UNIT,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('operations.weightUnit'),
            dataTestID: 'WeightUnit',
            options: weightUnitOptions,
            action: weightUnitOnChange,
            listHeader: 'Weight Unit',
            selectedOption: weightUnit,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_HEARING_PRECAUTIONS,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.hearingPrecautions'),
            dataTestId: 'HearingPrecautions',
            isChecked: hearingPrecautions,
            onChangeAction: setHearingPrecautions,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_ENVIRONMENTAL_CONTROLS,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.environmentalControls'),
            dataTestId: 'EnvironmentalControls',
            isChecked: environmentalControls,
            onChangeAction: setEnvironmentalControls,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_SAFETY_RESTRAINTS,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.safetyRestraints'),
            dataTestId: 'SafetyRestraints',
            isChecked: safetyRestraints,
            onChangeAction: setSafetyRestraints,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_SEIZURE_PRECAUTIONS,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.seizurePrecautions'),
            dataTestId: 'SeizurePrecautions',
            isChecked: seizurePrecautions,
            onChangeAction: setSeizurePrecautions,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PATIENT_LOADED_COLD,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.patientLoadedCold'),
            dataTestId: 'PatientLoadedCold',
            isChecked: patientLoadedCold,
            onChangeAction: setPatientLoadedCold,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PATIENT_LOADED_HOT,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.patientLoadedHot'),
            dataTestId: 'PatientLoadedHot',
            isChecked: patientLoadedHot,
            onChangeAction: setPatientLoadedHot,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PATIENT_OFFLOAD_COLD,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.patientOffloadCold'),
            dataTestId: 'Patient OffloadCold',
            isChecked: patientOffloadCold,
            onChangeAction: setPatientOffloadCold,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PATIENT_OFFLOAD_HOT,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.patientOffloadHot'),
            dataTestId: 'PatientOffloadHot',
            isChecked: patientOffloadHot,
            onChangeAction: setPatientOffloadHot,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PATIENT_MONITORING,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.patientMonitoring'),
            dataTestId: 'PatientMonitoring',
            isChecked: patientMonitoring,
            onChangeAction: setPatientMonitoring,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PEDIATRIC_STRETCHER_CHAIR,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.pediatricStretcherChair'),
            dataTestId: 'PediatricStretcherChair',
            isChecked: pediatricStretcherChair,
            onChangeAction: setPediatricStretcherChair,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PEDIATRIC_SAFETY_RESTRAINT_DEVICE,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.pediatricSafetyRestraintDevice'),
            dataTestId: 'PediatricSafetyRestraintDevice',
            isChecked: pediatricSafetyRestraintDevice,
            onChangeAction: setPediatricSafetyRestraintDevice,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_BABYPOD,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.babyPod'),
            dataTestId: 'BabyPod',
            isChecked: babyPod,
            onChangeAction: setBabyPod,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PEDIMATE,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.pediMate'),
            dataTestId: 'PediMate',
            isChecked: pediMate,
            onChangeAction: setPediMate,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: OperationsConstants.SET_PATIENT_PERSONAL_PROTECTIVE_EQUIPMENT,
          type: ProcedureConstants.TOGGLE,
          hidden: false,
          props: {
            rightLabel: t('operations.patientPersonalProtectiveEquipment'),
            dataTestId: 'PatientPersonalProtectiveEquipment',
            isChecked: patientPersonalProtectiveEquipment,
            onChangeAction: setPatientPersonalProtectiveEquipment,
          },
        },
      ],
    },
  ]

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'weightUnit']
    const currentState = filterOutObjectKeys(operations, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialOperationsState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='OperationsPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Operations</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'operations'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default Operations
